// Color palette grouped by color with shades, matching the design system

export type ColorShades = {
  [shade: string]: string // e.g., "50": "#F5F3FF"
}

export type ColorPalette = {
  purple: ColorShades
  blue: ColorShades
  green: ColorShades
  yellow: ColorShades
  orange: ColorShades
  red: ColorShades
  black: ColorShades
  deepNavy: ColorShades
  lime: ColorShades
  white: ColorShades
  // Add more as needed
  [key: string]: ColorShades
}

export const colorPalette: ColorPalette = {
  purple: {
    '50': '#f3efff',
    '100': '#dbccff',
    '200': '#c9b4ff',
    '300': '#b192ff',
    '400': '#a17dff',
    '500': '#8a5cff',
    '600': '#7e54e8',
  },
  blue: {
    '50': '#e9e9ff',
    '100': '#dfe0ff',
    '200': '#BDBEFF',
    '300': '#8C8EFF',
    '400': '#4c9af0',
    '500': '#4B4DED',
    '600': '#2F2FCC',
  },
  green: {
    '50': '#e8f7f1',
    '100': '#b6e5d5',
    '200': '#93d9c0',
    '300': '#62c7a4',
    '400': '#44bd92',
    '500': '#15ac77',
    '600': '#139d6c',
    '700': '#0f7a54',
    '800': '#0c5f41',
    '900': '#07443f',
  },
  yellow: {
    '50': '#fef9ed',
    '100': '#fdecc6',
    '200': '#fce2aa',
    '300': '#fbd583',
    '400': '#facd6b',
    '500': '#f9c146',
    '600': '#e3b040',
    '700': '#b18932',
    '800': '#896a27',
    '900': '#69511d',
  },
  orange: {
    '50': '#fff4ea',
    '100': '#ffefd2',
    '200': '#ffe2b8',
    '300': '#ffcd83',
    '400': '#ffbe5e',
    '500': '#ffa000',
    '600': '#cc7c00',
  },
  red: {
    '50': '#ffeff0',
    '100': '#ffcccf',
    '200': '#ffb4b8',
    '300': '#fe9198',
    '400': '#fe7c84',
    '500': '#fe5b65',
    '600': '#e7535c',
    '700': '#b44148',
    '800': '#8c3238',
    '900': '#6b262a',
  },
  black: {
    '50': '#e8e8e9',
    '100': '#b6b7ba',
    '200': '#939598',
    '300': '#626469',
    '400': '#43464c',
    '500': '#14181f',
    '600': '#12161c',
    '700': '#0e1116',
    '800': '#0b0d11',
    '900': '#080a0d',
  },
  deepNavy: {
    '50': '#f1f2f4',
    '100': '#d3d6dc',
    '200': '#bdc2cb',
    '300': '#9fa6b4',
    '400': '#8d95a5',
    '500': '#707a8f',
    '600': '#34314a',
    '700': '#1e1b2e',
    '800': '#3e434f',
    '900': '#2f333c',
  },
  lime: {
    '50': '#edffcc',
    '100': '#c0ff73',
  },
  white: {
    '0': '#ffffff',
    '50': '#fafafa',
    '100': '#f5f5f5',
    '200': '#e5e5e5',
    '300': '#a3a3a3',
  },
}

export const trendChartColor: string = colorPalette.white['300']

export const chartColors: string[] = [
  colorPalette.blue['500'],
  colorPalette.green['500'],
  colorPalette.orange['500'],
  colorPalette.red['500'],
  colorPalette.purple['300'],
  colorPalette.blue['300'],
  colorPalette.lime['100'],
  colorPalette.green['300'],
  colorPalette.orange['300'],
  colorPalette.red['300'],
  // random colors
  colorPalette.purple['400'],
  colorPalette.blue['400'],
  colorPalette.green['400'],
  colorPalette.orange['400'],
  colorPalette.red['400'],
]

export const metricCardColors: Record<string, { bg: string; color: string }> = {
  red500: { bg: 'bg-red-500', color: colorPalette.red['500'] },
  blue500: { bg: 'bg-blue-500', color: colorPalette.blue['500'] },
  green500: { bg: 'bg-green-500', color: colorPalette.green['500'] },
  orange500: { bg: 'bg-orange-500', color: colorPalette.orange['500'] },
  purple500: { bg: 'bg-purple-500', color: colorPalette.purple['500'] },
  red300: { bg: 'bg-red-300', color: colorPalette.red['300'] },
  blue300: { bg: 'bg-blue-300', color: colorPalette.blue['300'] },
  green300: { bg: 'bg-green-300', color: colorPalette.green['300'] },
  orange300: { bg: 'bg-orange-300', color: colorPalette.orange['300'] },
  purple300: { bg: 'bg-purple-300', color: colorPalette.purple['300'] },
}

export const lensMenuCardColors: Record<
  string,
  {
    bg: string
    border: { normal: string; isChild: string }
    icon: { bg: string; text: string }
  }
> = {
  red: {
    bg: 'bg-red-50',
    border: { normal: 'border-red-200', isChild: 'border-l-red-300' },
    icon: { bg: 'bg-red-50', text: 'text-red-500' },
  },
  blue: {
    bg: 'bg-blue-50',
    border: { normal: 'border-blue-200', isChild: 'border-l-blue-300' },
    icon: { bg: 'bg-blue-50', text: 'text-blue-500' },
  },
  green: {
    bg: 'bg-green-50',
    border: { normal: 'border-green-200', isChild: 'border-l-green-300' },
    icon: { bg: 'bg-green-50', text: 'text-green-500' },
  },
  orange: {
    bg: 'bg-orange-50',
    border: { normal: 'border-orange-200', isChild: 'border-l-orange-300' },
    icon: { bg: 'bg-orange-50', text: 'text-orange-500' },
  },
  purple: {
    bg: 'bg-purple-50',
    border: { normal: 'border-purple-200', isChild: 'border-l-purple-300' },
    icon: { bg: 'bg-purple-50', text: 'text-purple-500' },
  },
  yellow: {
    bg: 'bg-yellow-50',
    border: { normal: 'border-yellow-200', isChild: 'border-l-yellow-300' },
    icon: { bg: 'bg-yellow-50', text: 'text-yellow-500' },
  },
}
